import { useQueryClient } from '@tanstack/react-query';
import { useBizMutation, useBizQuery } from '../Hooks/useBizQuery';
import { Event, EventParticipator, ParticipationStatus, UpdateParticipationStatusResult } from '../Types/event';
import { User } from '../Types/user';
import { hasModuleAccess } from '../Services/access';
import { AccessTypeRight } from '../Types/access';
import { transformDateStringPropsToDate } from '../Utils/dateUtils';
import { get, post } from './common';
import { useEffect, useState } from 'react';
import { GenericCustomField } from '../Types/genericCustomField';

const QUERYKEY_UPCOMING_EVENTS = 'UPCOMING_EVENTS';
const QUERYKEY_EVENT_PARTICIPATION = 'EVENT_PARTICIPATION';
const QUERYKEY_ONE_EVENT = 'ONE_EVENT';
const QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES = 'EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES';

const eventEndpoint = 'api/event';

export function eventMapper(evnt: Event | null) {
	if (!evnt) return evnt;
	return transformDateStringPropsToDate<Event, keyof Event>(evnt, ['StartDateTimeUtc', 'EndDateTimeUtc']);
}

export function eventArrayMapper(events: Event[] | null) {
	if (!events) return events;
	return events.map((e) => eventMapper(e)) as Event[];
}

export const isUserAllowedToHandleEvent = (evnt: Event, user: User) => {
	if (hasModuleAccess(user, 'EVENT', AccessTypeRight.EditLimited)) return true;
	if (!evnt.CoOrganizers) return false;
	return evnt.CoOrganizers.map((co) => co.Id).includes(user.Id);
};

export const useGetUsersToCheckOffForEvent = (eventId: number) => {
	const { data } = useBizQuery([QUERYKEY_EVENT_PARTICIPATION, eventId], () => getUsersToCheckOffForEvent(eventId));
	return data ?? undefined;
};

export const useGetUpcomingEventsForUser = () => {
	const { data } = useBizQuery([QUERYKEY_UPCOMING_EVENTS], getUpcomingEventsForUser);
	return data ?? undefined;
};

export const useGetOneEvent = (id: number) => {
	const enabled = id > 0;
	const { data } = useBizQuery([QUERYKEY_ONE_EVENT, id], () => getOneEvent(id), enabled);
	return data ?? undefined;
};

export const useUpdateTodoCompletedStatus = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateTodoCompletedStatusWrapperFn = (params: { eventId: number; todoId: number; completed: boolean }) => {
		setUpdateEventId(params.eventId);

		//optimistic update
		const previousEvent = queryClient.getQueryData<Event>([QUERYKEY_ONE_EVENT, params.eventId]);

		if (previousEvent) {
			const updatedTodos = previousEvent.Todos?.map((todo) =>
				todo.Id === params.todoId ? { ...todo, IsCompleted: params.completed } : todo
			);

			const updatedEvent = { ...previousEvent, Todos: updatedTodos };

			queryClient.setQueryData([QUERYKEY_ONE_EVENT, params.eventId], updatedEvent);
		}

		return updateTodoCompletedStatus(params.eventId, params.todoId, params.completed);
	};

	const { mutate, statusState } = useBizMutation(updateTodoCompletedStatusWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		updateTodoCompletedStatus: (eventId: number, todoId: number, completed: boolean) =>
			mutate({ eventId, todoId, completed }),
		statusState,
	};
};

export const useUpdateParticipationStatus = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateParticipationStatusWrapperFn = (params: { eventId: number; status: ParticipationStatus; comment: string }) => {
		setUpdateEventId(params.eventId);

		//optimistic update
		const previousEvent = queryClient.getQueryData<Event>([QUERYKEY_ONE_EVENT, params.eventId]);

		if (previousEvent) {
			const updatedEvent = { ...previousEvent, UserStatus: params.status };
			queryClient.setQueryData([QUERYKEY_ONE_EVENT, params.eventId], updatedEvent);
		}

		return updateParticipationStatus(params.eventId, params.status, params.comment);
	};

	const { mutate, statusState } = useBizMutation(updateParticipationStatusWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		updateParticipationStatus: (eventId: number, status: ParticipationStatus) => mutate({ eventId, status, comment: '' }),
		statusState,
	};
};

export const useToggleParticipationStatusForRelatedUserGroup = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const toggleParticipationStatusForRelatedUserGroupWrapperFn = (params: {
		eventId: number;
		relatedGroupParticipantUserIds: number[];
	}) => {
		setUpdateEventId(params.eventId);

		//optimistic update
		const previousEvent = queryClient.getQueryData<Event>([QUERYKEY_ONE_EVENT, params.eventId]);

		if (previousEvent) {
			const updatedEvent = { ...previousEvent, RelatedParticipationUserIds: params.relatedGroupParticipantUserIds };
			queryClient.setQueryData([QUERYKEY_ONE_EVENT, params.eventId], updatedEvent);
		}

		return toggleParticipationStatusForRelatedUserGroup(params.eventId, params.relatedGroupParticipantUserIds);
	};

	const { mutate, statusState } = useBizMutation(toggleParticipationStatusForRelatedUserGroupWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		toggleParticipationStatusForRelatedUserGroup: (eventId: number, relatedGroupParticipantUserIds: number[]) =>
			mutate({ eventId, relatedGroupParticipantUserIds }),
		statusState,
	};
};

export const useUpdateConfirmedAttending = () => {
	const queryClient = useQueryClient();
	const [updateEventId, setUpdateEventId] = useState<number>(-1);

	const updateConfirmedAttendingWrapperFn = (params: {
		eventId: number;
		confirmedAttendedUserIds: number[];
		internalComment: string | null;
		completeEvent: boolean;
		participationReportCustomFields: GenericCustomField[] | null;
	}) => {
		setUpdateEventId(params.eventId);

		return updateConfirmedAttending(
			params.eventId,
			params.confirmedAttendedUserIds,
			params.internalComment,
			params.completeEvent,
			params.participationReportCustomFields
		);
	};

	const { mutate, statusState } = useBizMutation(updateConfirmedAttendingWrapperFn);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_EVENT_PARTICIPATION, updateEventId] });
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES, updateEventId] });
			queryClient.invalidateQueries({ queryKey: [QUERYKEY_ONE_EVENT, updateEventId] });
		}
	}, [statusState]);

	return {
		updateConfirmedAttending: (
			eventId: number,
			confirmedAttendedUserIds: number[],
			internalComment: string | null,
			completeEvent: boolean,
			participationReportCustomFields: GenericCustomField[] | null
		) => mutate({ eventId, confirmedAttendedUserIds, internalComment, completeEvent, participationReportCustomFields }),
		statusState,
	};
};

export const useGetParticipationReportCustomFieldValues = (eventId: number) => {
	const { data } = useBizQuery([QUERYKEY_EVENT_PARTICIPATION_REPORT_CUSTOMFIELD_VALUES, eventId], () =>
		getParticipationReportCustomFieldValues(eventId)
	);
	return data;
};

async function getUpcomingEventsForUser() {
	const endpoint = `${eventEndpoint}/GetUpcomingEventsForUser`;
	const result = await get<Event[]>(endpoint);
	return eventArrayMapper(result);
}

async function getOneEvent(id: number) {
	const endpoint = `${eventEndpoint}/GetOneEventForUser?eventId=${id}`;
	const result = await get<Event>(endpoint);
	return eventMapper(result);
}

async function updateTodoCompletedStatus(eventId: number, todoId: number, completed: boolean) {
	const endpoint = `${eventEndpoint}/UpdateTodoCompletedStatus?eventId=${eventId}&todoId=${todoId}&completed=${completed}`;
	await post(endpoint);
}

async function updateParticipationStatus(eventId: number, status: ParticipationStatus, comment: string) {
	const endpoint = `${eventEndpoint}/UpdateParticipationStatus`;
	await post(endpoint, { EventId: eventId, Status: status, Comment: comment });
}

async function toggleParticipationStatusForRelatedUserGroup(eventId: number, relatedGroupParticipantUserIds: number[]) {
	const endpoint = `${eventEndpoint}/ToggleParticipationStatusForRelatedUserGroup`;
	const response = await post<{ Success: boolean; ExtraInfo: UpdateParticipationStatusResult | null }>(endpoint, {
		EventId: eventId,
		RelatedGroupParticipantUserIds: relatedGroupParticipantUserIds,
	});
	return response;
}

async function getUsersToCheckOffForEvent(eventId: number) {
	const endpoint = `${eventEndpoint}/GetUsersToCheckOffForEvent?eventId=${eventId}`;
	const result = await get<EventParticipator[]>(endpoint);
	return result;
}

async function updateConfirmedAttending(
	eventId: number,
	confirmedAttendedUserIds: number[],
	internalComment: string | null,
	completeEvent: boolean,
	participationReportCustomFields: GenericCustomField[] | null
) {
	const endpoint = `${eventEndpoint}/UpdateConfirmedAttending`;
	const response = await post(endpoint, {
		EventId: eventId,
		ConfirmedAttendedUserIds: confirmedAttendedUserIds,
		Comment: internalComment,
		CompleteEvent: completeEvent,
		ParticipationReportCustomFields: participationReportCustomFields,
	});
	return response;
}

async function getParticipationReportCustomFieldValues(eventId: number) {
	const endpoint = `${eventEndpoint}/GetParticipationReportCustomFieldValues?eventId=${eventId}`;
	const result = await get<GenericCustomField[]>(endpoint);
	return result;
}
